import {
  closeCurrentDialog,
  IconButton,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, Icon, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {
  CodeResponseBody,
  useRemoveCodeLocalizedNameMutation,
  useRemoveCodeMutation,
  useRenameCodeMutation,
  useSetCodeLocalizedNameMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {buildCodelistMutationCalls} from '../utils/buildCodelistMutationCalls';
import {CodeForm, CodeFormProps} from './CodeForm';

interface CodelistItemProps {
  code: CodeResponseBody;
  codelistId: string;
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export function CodelistItem(props: CodelistItemProps) {
  const [renameCode] = useRenameCodeMutation();
  const [removeCode] = useRemoveCodeMutation();
  const [setCodeLocalizedName] = useSetCodeLocalizedNameMutation();
  const [removeCodeLocalizedName] = useRemoveCodeLocalizedNameMutation();

  const onEditSubmit: CodeFormProps['onSubmit'] = (values) => {
    const calls = buildCodelistMutationCalls({
      initialName: props.code.name,
      initialLocalizedNames: props.code.localizedNames ?? [],
      values,
    });

    if (calls.length === 0) {
      closeCurrentDialog();
      return Promise.resolve();
    }

    const promises = calls.map((call) =>
      match(call)
        .with({kind: 'rename'}, ({name}) =>
          renameCode({
            codelistId: props.codelistId,
            codeId: props.code.codeId,
            renameCodeRequestBody: {name},
          }).unwrap()
        )
        .with({kind: 'set'}, ({locale, name}) =>
          setCodeLocalizedName({
            codelistId: props.codelistId,
            codeId: props.code.codeId,
            setCodeLocalizedNameRequestBody: {locale, name},
          }).unwrap()
        )
        .with({kind: 'remove'}, ({locale}) =>
          removeCodeLocalizedName({
            codelistId: props.codelistId,
            codeId: props.code.codeId,
            locale,
          }).unwrap()
        )
        .exhaustive()
    );

    return Promise.all(promises)
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const onEdit = () => {
    openDialog(
      <CodeForm
        onSubmit={onEditSubmit}
        code={props.code}
        data-testid={testIds.settings.codelistDetail('code-form-edit')}
      />,
      {
        title: i18n.t('entity.codelist.labels.editItem'),
      }
    );
  };

  const onRemove = () => {
    openDeleteDialog({
      text: i18n.t('general.actions.confirmDeleteItem.sentence'),
      onConfirm: () =>
        removeCode({codelistId: props.codelistId, codeId: props.code.codeId})
          .unwrap()
          .then(() => showNotification.success(i18n.t('entity.codelist.notifications.itemDeleted')))
          .catch(handleApiError),
    });
  };

  return (
    <VStack>
      <Box paddingHorizontal={4}>
        <HStack minHeight={12} align="center" spacing={4}>
          {props.handleProps ? (
            <DragHandle {...props.handleProps}>
              <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
            </DragHandle>
          ) : (
            <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
          )}
          <Box flexGrow={1}>
            <Text>{props.code.name}</Text>
          </Box>
          <HStack>
            <IconButton
              icon="content/create"
              onClick={onEdit}
              data-testid={testIds.settings.codelistDetail('edit')}
            />
            <IconButton
              icon="action/delete"
              onClick={onRemove}
              data-testid={testIds.settings.codelistDetail('delete')}
            />
          </HStack>
        </HStack>
      </Box>
    </VStack>
  );
}

const DragHandle = styled.div`
  cursor: grab;
  display: flex;
  align-items: center;
  &:active {
    cursor: grabbing;
  }
`;
